/**
 * ServiceLogo — single source of truth for all service/brand icons used
 * across the pipeline node canvas and the Agent Hub workflow previews.
 *
 * Icon key conventions:
 *   'grok'         → xAI Grok / X
 *   'gemini'       → Google Gemini
 *   'google'       → Google brand mark
 *   'nano-banana'  → Nano Banana Pro
 *   'human'        → Human review gate
 *   'veo'          → Google Veo
 *   'elevenlabs'   → ElevenLabs TTS
 *   'ffmpeg'       → FFmpeg
 *   'heygen'       → HeyGen
 *   'perplexity'   → Perplexity API
 *   'x'            → X (Twitter) logo
 *   'globe'        → Generic geo/web
 *   'star'         → Multi-agent / premium
 *   'user'         → Generic user/avatar
 *   'settings'     → System/config
 *   'video'        → Short-video / generic video
 *   'clapperboard' → Cinematic workflow
 *   'brand-step-*' → Brand co-agent timeline step icons
 *   'ecom-step-*'  → Ecommerce co-agent timeline step icons
 *   'thought-kind-*' → Thought stream status icons
 *
 * Legacy aliases (kept for backward compat):
 *   'search' → 'grok', 'image' → 'nano-banana', 'mic' → 'elevenlabs', 'film' → 'ffmpeg'
 */

import React from "react";
import {
  Globe,
  Star,
  User,
  Settings,
  Video,
  Clapperboard,
  Database,
  Zap,
  Brain,
  BarChart2,
  Users,
  FileText,
  Mail,
  Package,
  LayoutDashboard,
  Megaphone,
} from "lucide-react";
import { R2, cdnUrl } from "@/lib/utils";

// ── Brand logo SVG helpers (defined outside component to avoid re-creation) ──
const TIMELINE_ICON_SRC: Record<string, string> = {
  "brand-step-input": `${R2}/icons/timeline/brand/brand-input.png`,
  "brand-step-research": `${R2}/icons/timeline/brand/market-research.png`,
  "brand-step-strategy": `${R2}/icons/timeline/brand/brand-strategy.png`,
  "brand-step-identity": `${R2}/icons/timeline/brand/brand-identity.png`,
  "brand-step-logo": `${R2}/icons/timeline/brand/logo-generation.png`,
  "brand-step-icons": `${R2}/icons/timeline/brand/icon-pack.png`,
  "brand-step-deck": `${R2}/icons/timeline/brand/brand-deck.png`,
  "ecom-step-estimates": cdnUrl("/icons/timeline/ecommerce/estimates.svg"),
  "ecom-step-content-inspiration": cdnUrl("/icons/timeline/ecommerce/content-inspiration.svg"),
  "thought-kind-thinking": cdnUrl("/icons/timeline/thought/thinking.svg"),
  "thought-kind-searching": cdnUrl("/icons/timeline/thought/searching.svg"),
  "thought-kind-analyzing": cdnUrl("/icons/timeline/thought/analyzing.svg"),
  "thought-kind-creating": cdnUrl("/icons/timeline/thought/creating.svg"),
  "thought-kind-discovered": cdnUrl("/icons/timeline/thought/discovered.svg"),
  "thought-kind-completed": cdnUrl("/icons/timeline/thought/completed.svg"),
  "thought-kind-waiting": cdnUrl("/icons/timeline/thought/waiting.svg"),
  "thought-kind-warning": cdnUrl("/icons/timeline/thought/warning.svg"),
  "thought-kind-error": cdnUrl("/icons/timeline/thought/error.svg"),
};

export const ServiceLogo = ({
  service,
  className = "w-5 h-5",
}: {
  service: string;
  className?: string;
}) => {
  const timelineIconSrc = TIMELINE_ICON_SRC[service];
  if (timelineIconSrc) {
    return (
      <img src={timelineIconSrc} alt="" className={className} style={{ objectFit: "contain" }} />
    );
  }

  switch (service) {
    // ── xAI Grok / X ───────────────────────────────────────────────────
    case "grok":
    case "search":
      return (
        <img
          src="https://pub-56b94dd8a0c946a689522e687290e19d.r2.dev/grok.svg"
          alt="Grok"
          className={className}
          style={{ objectFit: "contain" }}
        />
      );

    // ── Google Gemini ───────────────────────────────────────────────────
    case "gemini":
      return (
        <svg
          style={{ flex: "none", lineHeight: 1 }}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          className={className}
        >
          <title>Gemini</title>
          <path
            d="M20.616 10.835a14.147 14.147 0 01-4.45-3.001 14.111 14.111 0 01-3.678-6.452.503.503 0 00-.975 0 14.134 14.134 0 01-3.679 6.452 14.155 14.155 0 01-4.45 3.001c-.65.28-1.318.505-2.002.678a.502.502 0 000 .975c.684.172 1.35.397 2.002.677a14.147 14.147 0 014.45 3.001 14.112 14.112 0 013.679 6.453.502.502 0 00.975 0c.172-.685.397-1.351.677-2.003a14.145 14.145 0 013.001-4.45 14.113 14.113 0 016.453-3.678.503.503 0 000-.975 13.245 13.245 0 01-2.003-.678z"
            fill="#3186FF"
          />
          <path
            d="M20.616 10.835a14.147 14.147 0 01-4.45-3.001 14.111 14.111 0 01-3.678-6.452.503.503 0 00-.975 0 14.134 14.134 0 01-3.679 6.452 14.155 14.155 0 01-4.45 3.001c-.65.28-1.318.505-2.002.678a.502.502 0 000 .975c.684.172 1.35.397 2.002.677a14.147 14.147 0 014.45 3.001 14.112 14.112 0 013.679 6.453.502.502 0 00.975 0c.172-.685.397-1.351.677-2.003a14.145 14.145 0 013.001-4.45 14.113 14.113 0 016.453-3.678.503.503 0 000-.975 13.245 13.245 0 01-2.003-.678z"
            fill="url(#lobe-icons-gemini-fill-0)"
          />
          <path
            d="M20.616 10.835a14.147 14.147 0 01-4.45-3.001 14.111 14.111 0 01-3.678-6.452.503.503 0 00-.975 0 14.134 14.134 0 01-3.679 6.452 14.155 14.155 0 01-4.45 3.001c-.65.28-1.318.505-2.002.678a.502.502 0 000 .975c.684.172 1.35.397 2.002.677a14.147 14.147 0 014.45 3.001 14.112 14.112 0 013.679 6.453.502.502 0 00.975 0c.172-.685.397-1.351.677-2.003a14.145 14.145 0 013.001-4.45 14.113 14.113 0 016.453-3.678.503.503 0 000-.975 13.245 13.245 0 01-2.003-.678z"
            fill="url(#lobe-icons-gemini-fill-1)"
          />
          <path
            d="M20.616 10.835a14.147 14.147 0 01-4.45-3.001 14.111 14.111 0 01-3.678-6.452.503.503 0 00-.975 0 14.134 14.134 0 01-3.679 6.452 14.155 14.155 0 01-4.45 3.001c-.65.28-1.318.505-2.002.678a.502.502 0 000 .975c.684.172 1.35.397 2.002.677a14.147 14.147 0 014.45 3.001 14.112 14.112 0 013.679 6.453.502.502 0 00.975 0c.172-.685.397-1.351.677-2.003a14.145 14.145 0 013.001-4.45 14.113 14.113 0 016.453-3.678.503.503 0 000-.975 13.245 13.245 0 01-2.003-.678z"
            fill="url(#lobe-icons-gemini-fill-2)"
          />
          <defs>
            <linearGradient
              gradientUnits="userSpaceOnUse"
              id="lobe-icons-gemini-fill-0"
              x1="7"
              x2="11"
              y1="15.5"
              y2="12"
            >
              <stop stopColor="#08B962" />
              <stop offset="1" stopColor="#08B962" stopOpacity="0" />
            </linearGradient>
            <linearGradient
              gradientUnits="userSpaceOnUse"
              id="lobe-icons-gemini-fill-1"
              x1="8"
              x2="11.5"
              y1="5.5"
              y2="11"
            >
              <stop stopColor="#F94543" />
              <stop offset="1" stopColor="#F94543" stopOpacity="0" />
            </linearGradient>
            <linearGradient
              gradientUnits="userSpaceOnUse"
              id="lobe-icons-gemini-fill-2"
              x1="3.5"
              x2="17.5"
              y1="13.5"
              y2="12"
            >
              <stop stopColor="#FABC12" />
              <stop offset=".46" stopColor="#FABC12" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
      );

    // ── Google Brand ────────────────────────────────────────────────────
    case "google":
      return (
        <img
          src="/icons/services/google-color.svg"
          alt="Google"
          className={className}
          style={{ objectFit: "contain" }}
        />
      );

    // ── X / Twitter ─────────────────────────────────────────────────────
    case "x":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );

    // ── Nano Banana Pro ─────────────────────────────────────────────────
    case "nano-banana":
    case "image":
      return (
        <span className="leading-none select-none text-2xl filter drop-shadow-[0_0_4px_rgba(234,179,8,0.4)]">
          🍌
        </span>
      );

    // ── Human review gate ───────────────────────────────────────────────
    case "human":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`${className} text-orange-400`}
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );

    // ── Google Veo ──────────────────────────────────────────────────────
    case "veo":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
      );

    // ── ElevenLabs TTS ──────────────────────────────────────────────────
    case "elevenlabs":
    case "mic":
      return (
        <svg
          fill="currentColor"
          fillRule="evenodd"
          style={{ flex: "none", lineHeight: 1 }}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          className={`${className} text-black`}
        >
          <title>ElevenLabs</title>
          <path d="M5 0h5v24H5V0zM14 0h5v24h-5V0z" />
        </svg>
      );

    // ── FFmpeg ──────────────────────────────────────────────────────────
    // Three diagonal parallelogram bands (green / orange / red) on dark bg.
    case "ffmpeg":
    case "film":
      return (
        <svg viewBox="0 0 24 24" className={className}>
          <rect width="24" height="24" rx="3" fill="#0d1117" />
          <polygon points="0,6  8,6  12,18  4,18" fill="#22c55e" />
          <polygon points="8,6  16,6 20,18 12,18" fill="#f97316" />
          <polygon points="16,6 24,6 24,18 20,18" fill="#ef4444" />
        </svg>
      );

    // ── HeyGen ──────────────────────────────────────────────────────────
    case "heygen":
      return (
        <img
          src="https://pub-56b94dd8a0c946a689522e687290e19d.r2.dev/heygen.png"
          alt="HeyGen"
          className={className}
          style={{ objectFit: "contain" }}
        />
      );

    // ── Perplexity API ──────────────────────────────────────────────────
    case "perplexity":
      return (
        <svg
          fill="currentColor"
          fillRule="evenodd"
          style={{ flex: "none", lineHeight: 1 }}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          className={`${className} text-[#22B8CD]`}
        >
          <title>Perplexity</title>
          <path d="M19.785 0v7.272H22.5V17.62h-2.935V24l-7.037-6.194v6.145h-1.091v-6.152L4.392 24v-6.465H1.5V7.188h2.884V0l7.053 6.494V.19h1.09v6.49L19.786 0zm-7.257 9.044v7.319l5.946 5.234V14.44l-5.946-5.397zm-1.099-.08l-5.946 5.398v7.235l5.946-5.234V8.965zm8.136 7.58h1.844V8.349H13.46l6.105 5.54v2.655zm-8.982-8.28H2.59v8.195h1.8v-2.576l6.192-5.62zM5.475 2.476v4.71h5.115l-5.115-4.71zm13.219 0l-5.115 4.71h5.115v-4.71z" />
        </svg>
      );

    // ── ChatGPT / OpenAI ────────────────────────────────────────────────
    case "chatgpt":
    case "openai":
      return (
        <svg
          fill="currentColor"
          fillRule="evenodd"
          style={{ flex: "none", lineHeight: 1 }}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          className={`${className} text-black`}
        >
          <title>OpenAI</title>
          <path d="M9.205 8.658v-2.26c0-.19.072-.333.238-.428l4.543-2.616c.619-.357 1.356-.523 2.117-.523 2.854 0 4.662 2.212 4.662 4.566 0 .167 0 .357-.024.547l-4.71-2.759a.797.797 0 00-.856 0l-5.97 3.473zm10.609 8.8V12.06c0-.333-.143-.57-.429-.737l-5.97-3.473 1.95-1.118a.433.433 0 01.476 0l4.543 2.617c1.309.76 2.189 2.378 2.189 3.948 0 1.808-1.07 3.473-2.76 4.163zM7.802 12.703l-1.95-1.142c-.167-.095-.239-.238-.239-.428V5.899c0-2.545 1.95-4.472 4.591-4.472 1 0 1.927.333 2.712.928L8.23 5.067c-.285.166-.428.404-.428.737v6.898zM12 15.128l-2.795-1.57v-3.33L12 8.658l2.795 1.57v3.33L12 15.128zm1.796 7.23c-1 0-1.927-.332-2.712-.927l4.686-2.712c.285-.166.428-.404.428-.737v-6.898l1.974 1.142c.167.095.238.238.238.428v5.233c0 2.545-1.974 4.472-4.614 4.472zm-5.637-5.303l-4.544-2.617c-1.308-.761-2.188-2.378-2.188-3.948A4.482 4.482 0 014.21 6.327v5.423c0 .333.143.571.428.738l5.947 3.449-1.95 1.118a.432.432 0 01-.476 0zm-.262 3.9c-2.688 0-4.662-2.021-4.662-4.519 0-.19.024-.38.047-.57l4.686 2.71c.286.167.571.167.856 0l5.97-3.448v2.26c0 .19-.07.333-.237.428l-4.543 2.616c-.619.357-1.356.523-2.117.523zm5.899 2.83a5.947 5.947 0 005.827-4.756C22.287 18.339 24 15.84 24 13.296c0-1.665-.713-3.282-1.998-4.448.119-.5.19-.999.19-1.498 0-3.401-2.759-5.947-5.946-5.947-.642 0-1.26.095-1.88.31A5.962 5.962 0 0010.205 0a5.947 5.947 0 00-5.827 4.757C1.713 5.447 0 7.945 0 10.49c0 1.666.713 3.283 1.998 4.448-.119.5-.19 1-.19 1.499 0 3.401 2.759 5.946 5.946 5.946.642 0 1.26-.095 1.88-.309a5.96 5.96 0 004.162 1.713z" />
        </svg>
      );

    // ── YouTube ─────────────────────────────────────────────────────────
    case "youtube":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path
            d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"
            fill="#FF0000"
          />
          <path d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#ffffff" />
        </svg>
      );

    // ── TikTok ──────────────────────────────────────────────────────────
    case "tiktok":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={`${className} text-black`}>
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.77 1.52V6.76a4.85 4.85 0 01-1-.07z" />
        </svg>
      );

    // ── Instagram ───────────────────────────────────────────────────────
    case "instagram":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className={className}
          style={{ color: "#E1306C" }}
        >
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
      );

    // ── Zapier ──────────────────────────────────────────────────────────
    case "zapier":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className={`${className} text-[#FF4A00]`}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm5.25 13.773h-3.965l2.8 2.798a.833.833 0 01-1.178 1.178l-2.8-2.798v3.965a.833.833 0 01-1.664 0v-3.965l-2.799 2.799a.833.833 0 01-1.178-1.178l2.799-2.799H5.3a.833.833 0 010-1.664h3.965L6.466 9.31a.833.833 0 011.178-1.178l2.799 2.799V6.966a.833.833 0 011.664 0v3.965l2.8-2.799a.833.833 0 011.178 1.178l-2.8 2.799h3.965a.833.833 0 010 1.664z" />
        </svg>
      );

    // ── HubSpot ─────────────────────────────────────────────────────────
    case "hubspot":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className={`${className} text-[#FF7A59]`}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M16.796 7.342V5.18a1.71 1.71 0 00.985-1.542V3.59A1.71 1.71 0 0016.07 1.88h-.048a1.71 1.71 0 00-1.71 1.71v.048a1.71 1.71 0 00.985 1.542v2.162a4.853 4.853 0 00-2.306 1.015L6.85 4.066a1.896 1.896 0 10-.88 1.307l6.016 4.205a4.853 4.853 0 00-.661 2.433 4.853 4.853 0 00.661 2.433l-1.832 1.283a1.542 1.542 0 10.88 1.306l2.097-1.468a4.87 4.87 0 007.17-4.302 4.87 4.87 0 00-3.505-4.921zm-1.274 7.803a2.882 2.882 0 110-5.764 2.882 2.882 0 010 5.764z" />
        </svg>
      );

    // ── Braze ───────────────────────────────────────────────────────────
    case "braze":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className={`${className} text-[#3CAEA3]`}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm4.5 16.5h-9a.75.75 0 010-1.5h9a.75.75 0 010 1.5zm0-4h-9a.75.75 0 010-1.5h9a.75.75 0 010 1.5zm0-4h-9a.75.75 0 010-1.5h9a.75.75 0 010 1.5z" />
        </svg>
      );

    // ── Klaviyo ─────────────────────────────────────────────────────────
    case "klaviyo":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className={`${className} text-[#000000] dark:text-white`}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M18 3H6a3 3 0 00-3 3v12a3 3 0 003 3h12a3 3 0 003-3V6a3 3 0 00-3-3zM8 17V7l8 5-8 5z" />
        </svg>
      );

    // ── Mixpanel ────────────────────────────────────────────────────────
    case "mixpanel":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className={`${className} text-[#7856FF]`}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 3a7 7 0 110 14A7 7 0 0112 5zm-1 3v4H7l5 5 5-5h-4V8h-1z" />
        </svg>
      );

    // ── Amplitude ───────────────────────────────────────────────────────
    case "amplitude":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className={`${className} text-[#00A0FE]`}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 13H9V9h2v6zm4 0h-2V7h2v8z" />
        </svg>
      );

    // ── Google Ads ──────────────────────────────────────────────────────
    case "google-ads":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className={className}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.5 17L9 5l3.25 5.5L9.75 14H15L10 5l6.5 11"
            stroke="#FBBC05"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="20" cy="17" r="2.5" fill="#34A853" />
          <circle cx="4" cy="17" r="2.5" fill="#4285F4" />
        </svg>
      );

    // ── Google Analytics (GA4) ──────────────────────────────────────────
    case "ga4":
    case "google-analytics":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className={className}
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="3" y="13" width="4" height="8" rx="1.5" fill="#F9AB00" />
          <rect x="10" y="7" width="4" height="14" rx="1.5" fill="#E37400" />
          <rect x="17" y="2" width="4" height="19" rx="1.5" fill="#F4B400" />
        </svg>
      );

    // ── Google Tag Manager ──────────────────────────────────────────────
    case "gtm":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className={className}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 2L2 7.5V16.5L12 22L22 16.5V7.5L12 2Z" fill="#8AB4F8" />
          <path d="M12 6L6 9.5V14.5L12 18L18 14.5V9.5L12 6Z" fill="#4285F4" />
          <path d="M12 9L9.5 10.5V13.5L12 15L14.5 13.5V10.5L12 9Z" fill="#1967D2" />
        </svg>
      );

    // ── Email / Mail / Gmail ────────────────────────────────────────────
    case "mail":
      return <Mail className={`${className} text-blue-400`} />;
    case "gmail":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className={className}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z"
            fill="#EA4335"
          />
        </svg>
      );

    // ── Wiser Studio ────────────────────────────────────────────────────
    case "wiser":
      return (
        <div
          className={`${className} bg-foreground rounded-md flex items-center justify-center shadow-sm`}
        >
          <div className="w-1/3 h-1/3 rounded-sm bg-background" />
        </div>
      );

    // ── MCP (Model Context Protocol) ─────────────────────────────────────
    case "mcp":
      return (
        <svg
          fill="currentColor"
          fillRule="evenodd"
          className={`${className} text-indigo-500 dark:text-indigo-400`}
          style={{ flex: "none", lineHeight: 1 }}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>ModelContextProtocol</title>
          <path d="M15.688 2.343a2.588 2.588 0 00-3.61 0l-9.626 9.44a.863.863 0 01-1.203 0 .823.823 0 010-1.18l9.626-9.44a4.313 4.313 0 016.016 0 4.116 4.116 0 011.204 3.54 4.3 4.3 0 013.609 1.18l.05.05a4.115 4.115 0 010 5.9l-8.706 8.537a.274.274 0 000 .393l1.788 1.754a.823.823 0 010 1.18.863.863 0 01-1.203 0l-1.788-1.753a1.92 1.92 0 010-2.754l8.706-8.538a2.47 2.47 0 000-3.54l-.05-.049a2.588 2.588 0 00-3.607-.003l-7.172 7.034-.002.002-.098.097a.863.863 0 01-1.204 0 .823.823 0 010-1.18l7.273-7.133a2.47 2.47 0 00-.003-3.537z"></path>
          <path d="M14.485 4.703a.823.823 0 000-1.18.863.863 0 00-1.204 0l-7.119 6.982a4.115 4.115 0 000 5.9 4.314 4.314 0 006.016 0l7.12-6.982a.823.823 0 000-1.18.863.863 0 00-1.204 0l-7.119 6.982a2.588 2.588 0 01-3.61 0 2.47 2.47 0 010-3.54l7.12-6.982z"></path>
        </svg>
      );

    // ── Ideogram ────────────────────────────────────────────────────────
    case "ideogram":
      return (
        <svg
          fill="currentColor"
          fillRule="evenodd"
          className={className}
          style={{ flex: "none", lineHeight: 1 }}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>Ideogram</title>
          <path d="M17.768 1.482a6.4 6.4 0 012.988 3.78c.19.068.377.154.555.257.026.015.05.03.073.047.226.137.436.301.627.49a3.377 3.377 0 01-.105 4.875 3.376 3.376 0 01-.216 5.158l-.037.029.021.032c.39.602.585 1.324.534 2.065l-.009.11a3.377 3.377 0 01-4.658 2.777 3.377 3.377 0 01-5.846.971 4.052 4.052 0 01-1.635.397c-.04.005-.082.008-.125.008h-.897l-.068-.003c-1.116-.067-1.093-1.775.068-1.775h.804l.046-.004.047-.001a2.3 2.3 0 00.079-4.6.735.735 0 01-.08.005H2.808a.889.889 0 010-1.778h7.034l.046-.004.048-.001a2.3 2.3 0 00.509-4.544l-.141-.031a2.188 2.188 0 00-.299-.046.759.759 0 01-.07.003H2.808a.889.889 0 110-1.777h7.035l.046-.004.047-.001a2.3 2.3 0 00.087-4.6.972.972 0 01-.087.004h-.897L8.97 3.32c-1.116-.067-1.093-1.775.068-1.775h.8a.9.9 0 01.097-.005c.402 0 .79.058 1.157.166l.031-.02a6.399 6.399 0 016.645-.203zm-3.737 11.484l-.068.036-.083.051a4.08 4.08 0 01-1.404 2.153 4.07 4.07 0 011.537 3.189 4.06 4.06 0 01-.92 2.579 1.599 1.599 0 002.824-1.322l-.03-.124-.013-.079-.005-.056-.001-.042.001-.058.005-.053.013-.078.018-.072.022-.064.035-.079.005-.01a.869.869 0 01.158-.217l.055-.052.041-.032.05-.035.042-.026.055-.03.073-.03.056-.02.04-.011.078-.016.073-.009.045-.002h.048l.041.002.058.006.056.01.062.015.031.01.049.017.054.023.044.021.053.031.035.023c.06.042.114.09.163.147l.038.046.034.048a1.6 1.6 0 102.4-2.07 3.256 3.256 0 01-.473.007l-.057-.005-.026-.005h-.016a3.376 3.376 0 01-2.985-2.568l-.026-.116a1.6 1.6 0 00-2.285-1.133zm-4.416 4.595a.889.889 0 110 1.777H5.939a.889.889 0 010-1.777h3.676zm9.208-10.503a1.6 1.6 0 00-.797 1.3l-.002.085a.889.889 0 01-.83.887l-.059.002a.889.889 0 01-.887-.83l-.002-.059a1.6 1.6 0 00-2.528-1.301 4.089 4.089 0 01-1.255 1.674 4.083 4.083 0 011.451 2.308 3.374 3.374 0 014.125 2.532l.02.093a1.6 1.6 0 001.395 1.26l.124.01.042.002c.06 0 .12-.003.18-.01l.085-.011.062-.015a1.6 1.6 0 00-.245-3.163l-.079-.002a.887.887 0 01-.683-.32l-.034-.044a.884.884 0 01-.154-.702.889.889 0 01.807-.71l.064-.001a1.6 1.6 0 001.134-2.728 1.6 1.6 0 00-.786-.426l-.102-.019a.856.856 0 01-.076-.016 1.592 1.592 0 00-.97.204zm-9.199 4.08a.889.889 0 010 1.778H.89a.889.889 0 010-1.777h8.735zm-.01-6.432a.889.889 0 110 1.777H5.94a.889.889 0 110-1.777h3.676zm3.297-1.954l-.053.021a4.065 4.065 0 011.126 2.36 3.375 3.375 0 013.151 1.027 3.366 3.366 0 011.695-1 4.621 4.621 0 00-5.919-2.408z" />
        </svg>
      );

    // ── Recraft ─────────────────────────────────────────────────────────
    case "recraft":
      return (
        <svg
          fill="currentColor"
          fillRule="evenodd"
          className={className}
          style={{ flex: "none", lineHeight: 1 }}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>Recraft</title>
          <path d="M19.667 8.275c0-4.57-4.15-8.275-9.27-8.275-1.774 0-3.213 3.705-3.213 8.275 0 1.143.09 2.233.253 3.224H4.29L1 23h9.4v-6.447c5.117 0 9.266-3.707 9.266-8.275l.001-.002zm-9.27-6.76c.93 0 1.682 3.028 1.682 6.76 0 3.733-.752 6.76-1.681 6.76-.93 0-1.681-3.027-1.681-6.76 0-3.732.752-6.76 1.68-6.76z" />
          <path d="M19.848 16.552h-9.44L14.028 23h9.438l-3.618-6.448z" />
        </svg>
      );

    // ── Shopify ───────────────────────────────────────────────────────
    case "shopify":
    case "store":
      return (
        <svg viewBox="0 0 24 24" className={className} xmlns="http://www.w3.org/2000/svg">
          <path
            d="M11.17.83c.136 0 .271.038.405.135-.984.465-2.064 1.639-2.508 3.992-.656.213-1.293.405-1.889.578C7.697 3.75 8.951.84 11.17.84V.83zm1.235 2.949v.135c-.754.232-1.583.484-2.394.736.466-1.777 1.333-2.645 2.085-2.971.193.501.309 1.176.309 2.1zm.539-2.234c.694.074 1.141.867 1.429 1.755-.349.114-.735.231-1.158.366v-.252c0-.752-.096-1.371-.271-1.871v.002zm2.992 1.289c-.02 0-.06.021-.078.021s-.289.075-.714.21c-.423-1.233-1.176-2.37-2.508-2.37h-.115C12.135.209 11.669 0 11.265 0 8.159 0 6.675 3.877 6.21 5.846c-1.194.365-2.063.636-2.16.674-.675.213-.694.232-.772.87-.075.462-1.83 14.063-1.83 14.063L15.009 24l.927-21.166z"
            fill="#95BF47"
          />
          <path
            d="M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.018-.116-.114-.192-.211-.192s-1.929-.136-1.929-.136-1.275-1.274-1.439-1.411c-.045-.037-.075-.057-.121-.074l-.914 21.104h.023z"
            fill="#5E8E3E"
          />
          <path
            d="M11.71 11.305s-.81-.424-1.774-.424c-1.447 0-1.504.906-1.504 1.141 0 1.232 3.24 1.715 3.24 4.629 0 2.295-1.44 3.76-3.406 3.76-2.354 0-3.54-1.465-3.54-1.465l.646-2.086s1.245 1.066 2.28 1.066c.675 0 .975-.545.975-.932 0-1.619-2.654-1.694-2.654-4.359-.034-2.237 1.571-4.416 4.827-4.416 1.257 0 1.875.361 1.875.361l-.945 2.715-.02.01z"
            fill="#fff"
          />
        </svg>
      );

    // ── Amazon ────────────────────────────────────────────────────────
    case "amazon":
      return (
        <svg viewBox="0 0 24 24" className={className} xmlns="http://www.w3.org/2000/svg">
          <path
            d="M.045 18.02c.072-.116.187-.124.348-.022 3.636 2.11 7.594 3.166 11.87 3.166 2.852 0 5.668-.533 8.447-1.595l.315-.14c.138-.06.234-.1.293-.13.226-.088.39-.046.525.13.12.174.09.336-.12.48-.256.19-.6.41-1.006.654-1.244.743-2.64 1.316-4.185 1.726a17.617 17.617 0 01-10.951-.577 17.88 17.88 0 01-5.43-3.35c-.1-.074-.151-.15-.151-.22 0-.047.021-.09.051-.13zm6.565-6.218c0-1.005.247-1.863.743-2.577.495-.71 1.17-1.25 2.04-1.615.796-.335 1.756-.575 2.912-.72.39-.046 1.033-.103 1.92-.174v-.37c0-.93-.105-1.558-.3-1.875-.302-.43-.78-.65-1.44-.65h-.182c-.48.046-.896.196-1.246.46-.35.27-.575.63-.675 1.096-.06.3-.206.465-.435.51l-2.52-.315c-.248-.06-.372-.18-.372-.39 0-.046.007-.09.022-.15.247-1.29.855-2.25 1.82-2.88.976-.616 2.1-.975 3.39-1.05h.54c1.65 0 2.957.434 3.888 1.29.135.15.27.3.405.48.12.165.224.314.283.45.075.134.15.33.195.57.06.254.105.42.135.51.03.104.062.3.076.615.01.313.02.493.02.553v5.28c0 .376.06.72.165 1.036.105.313.21.54.315.674l.51.674c.09.136.136.256.136.36 0 .12-.06.226-.18.314-1.2 1.05-1.86 1.62-1.963 1.71-.165.135-.375.15-.63.045a6.062 6.062 0 01-.526-.496l-.31-.347a9.391 9.391 0 01-.317-.42l-.3-.435c-.81.886-1.603 1.44-2.4 1.665-.494.15-1.093.227-1.83.227-1.11 0-2.04-.343-2.76-1.034-.72-.69-1.08-1.665-1.08-2.94l-.05-.076zm3.753-.438c0 .566.14 1.02.425 1.364.285.34.675.512 1.155.512.045 0 .106-.007.195-.02.09-.016.134-.023.166-.023.614-.16 1.08-.553 1.424-1.178.165-.28.285-.58.36-.91.09-.32.12-.59.135-.8.015-.195.015-.54.015-1.005v-.54c-.84 0-1.484.06-1.92.18-1.275.36-1.92 1.17-1.92 2.43l-.035-.02zm9.162 7.027c.03-.06.075-.11.132-.17.362-.243.714-.41 1.05-.5a8.094 8.094 0 011.612-.24c.14-.012.28 0 .41.03.65.06 1.05.168 1.172.33.063.09.099.228.099.39v.15c0 .51-.149 1.11-.424 1.8-.278.69-.664 1.248-1.156 1.68-.073.06-.14.09-.197.09-.03 0-.06 0-.09-.012-.09-.044-.107-.12-.064-.24.54-1.26.806-2.143.806-2.64 0-.15-.03-.27-.087-.344-.145-.166-.55-.257-1.224-.257-.243 0-.533.016-.87.046-.363.045-.7.09-1 .135-.09 0-.148-.014-.18-.044-.03-.03-.036-.047-.02-.077 0-.017.006-.03.02-.063v-.06z"
            fill="currentColor"
          />
        </svg>
      );

    // ── Generic / Lucide fallbacks ──────────────────────────────────────
    // Marketing Operator nodes
    case "database":
      return <Database className={`${className} text-emerald-400`} />;
    case "zap":
      return <Zap className={`${className} text-emerald-400`} />;
    case "brain":
      return <Brain className={`${className} text-emerald-400`} />;
    case "bar-chart":
      return <BarChart2 className={`${className} text-emerald-400`} />;
    case "users":
      return <Users className={`${className} text-emerald-400`} />;
    case "file-text":
      return <FileText className={`${className} text-emerald-400`} />;

    // Existing Lucide fallbacks
    case "globe":
      return <Globe className={`${className} text-blue-400`} />;
    case "star":
      return <Star className={`${className} text-yellow-400`} />;
    case "user":
      return <User className={`${className} text-indigo-400`} />;
    case "settings":
      return <Settings className={`${className} text-gray-400`} />;
    case "video":
      return <Video className={`${className} text-blue-400`} />;
    case "clapperboard":
      return <Clapperboard className={`${className} text-purple-400`} />;
    case "package":
      return <Package className={`${className} text-orange-400`} />;
    case "layout":
      return <LayoutDashboard className={`${className} text-cyan-400`} />;
    case "megaphone":
      return <Megaphone className={`${className} text-pink-400`} />;

    default:
      return <div className={`${className} bg-gray-500 rounded`} />;
  }
};
